import {Box, Container, Grid, Typography, LinearProgress, Card, CardContent} from '@mui/material';
import {UsagePlanLabel} from '@/types/UsagePlan';
import {useUserData} from '@/hooks/use-user-data-provider';
import {FileText, FileUp} from 'lucide-react';
import {Link} from 'react-router-dom';
import dayjs from 'dayjs';

const UsageCard = ({
                     title,
                     used,
                     limit,
                     icon: Icon,
                     isUnlimited = false
                   }: {
  title: string;
  used: number;
  limit: number;
  icon: React.ElementType;
  isUnlimited?: boolean;
}) => {
  const percentage = isUnlimited ? 0 : Math.min((used / limit) * 100, 100);
  const remaining = limit - used;
  
  return (
    <Card sx={{height: '100%'}}>
      <CardContent>
        <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
          <Icon size={24} style={{marginRight: '8px'}}/>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        </Box>
        {!isUnlimited && (
          <Box sx={{mb: 2}}>
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                  backgroundColor: percentage > 80 ? '#f44336' : '#2196f3',
                },
              }}
            />
          </Box>
        )}
        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
          <Typography color="text.secondary">
            使用済み: {used}
          </Typography>
          {!isUnlimited && (
            <Typography color="text.secondary">
              残り: {remaining}
            </Typography>
          )}
        </Box>
        {!isUnlimited ? (
          <Typography variant="body2" color="text.secondary" sx={{mt: 1, textAlign: 'right'}}>
            無料枠: {limit}件
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{mt: 1, textAlign: 'right'}}>
            {used >= limit ? "無料枠を超過しました" : `無料枠: ${limit}件`}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export function UsagePage() {
  const {user} = useUserData();
  const usage = user?.organization?.usage;
  const usageLimit = user?.organization?.usageLimit;
  
  if (!usage || !usageLimit) {
    return (
      <Container maxWidth="lg" sx={{mt: 4, mb: 4}}>
        <Typography>利用状況を取得できません</Typography>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{mt: 4, mb: 4}}>
      <Typography variant="h4" component="h1" gutterBottom sx={{mb: 4}}>
        利用状況
      </Typography>
      
      <Box sx={{mb: 4}}>
        <Typography mb={4}>
          疑問点、追加のご要望はお気軽に<Link to={"/contact"}>問い合わせフォーム</Link>よりお問い合わせください。
        </Typography>
        <Typography variant="h5" gutterBottom color="text.secondary">
          {user?.organization?.name}様
        </Typography>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            color: usageLimit.plan === 'Free' ? 'primary.main' : 'secondary.main',
            fontWeight: 'bold',
            my: 2
          }}
        >
          現在のプラン: {UsagePlanLabel[usageLimit.plan]}
        </Typography>
        <Typography color="text.secondary">
          {dayjs(usage.month).format('YYYY年M月')}の利用状況
        </Typography>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <UsageCard
            title="重説作成"
            used={usage.zyusetuCount}
            limit={usageLimit.zyusetuCount}
            icon={FileText}
            isUnlimited={usage.zyusetuCount >= usageLimit.zyusetuCount}
          />
        </Grid>
        {/*<Grid item xs={12} md={4}>*/}
        {/*  <_UsageCard*/}
        {/*    title="図面解析"*/}
        {/*    used={usage.zumenAnalyzeCount}*/}
        {/*    limit={usageLimit.zumenAnalyzeCount}*/}
        {/*    icon={FileSearch}*/}
        {/*  />*/}
        {/*</Grid>*/}
        <Grid item xs={12} md={4}>
          <UsageCard
            title="管理規約データ抽出ツール"
            used={usage.managementByRawAnalyzeCount}
            limit={usageLimit.managementByRawAnalyzeCount}
            icon={FileUp}
          />
        </Grid>
      </Grid>
    </Container>
  );
}
